import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import { useAuth } from '../contexts/AuthContext';
import { useUserType } from '../hooks/useUserType';
import { supabase } from '../lib/supabaseClient';
import { PageHeader } from '../components/navigation';
import { ModernCard } from '../components/ui';
import { CourseCard } from '../components/ui';
import { KnowledgeLibrary } from '../components/KnowledgeLibrary';
import AIContentRecommendationEngine from '../components/AIContentRecommendationEngine';
import ProactiveGuidanceSystem from '../components/ProactiveGuidanceSystem';
import SmartRecommendations from '../components/SmartRecommendations';
import { ComingSoonBadge } from '../components/ComingSoonBadge';
import { 
  BookOpen, 
  Search, 
  Filter, 
  Star, 
  Clock, 
  TrendingUp,
  Bookmark,
  Play,
  CheckCircle,
  Award,
  Target,
  Users,
  MessageSquare,
  ChevronRight,
  Sparkles,
  FileText
} from 'lucide-react';

const LearnPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userType, loading: userTypeLoading } = useUserType();
  const [activeTab, setActiveTab] = useState<'recommendations' | 'browse' | 'saved'>('recommendations');
  const [searchTerm, setSearchTerm] = useState('');
  const [profile, setProfile] = useState<any>(null);

  useEffect(() => {
    loadProfile();
  }, [user]);

  const loadProfile = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) {
        console.error('Error loading profile:', error);
        return;
      }
      
      setProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  // SEO optimization
  useSEO({
    title: 'Learn - MyNewStart',
    description: 'Discover personalized learning recommendations, browse courses, and track your progress.',
    keywords: ['learn', 'courses', 'education', 'recommendations', 'progress'],
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Learn', url: '/learn' }
    ]
  });

  const tabs = [
    { id: 'recommendations', name: 'Recommendations', icon: Sparkles },
    { id: 'browse', name: 'Browse', icon: BookOpen },
    { id: 'saved', name: 'Saved', icon: Bookmark }
  ];

  const recommendations = [
    {
      id: 'banking-system',
      title: 'Banking Basics for New Immigrants',
      description: 'Learn how to open accounts, use debit cards, and manage your money safely.',
      duration: '12 min',
      level: 'Beginner',
      progress: 0,
      whyThis: 'Based on your profile, you\'re ready to set up banking',
      category: 'Banking & Credit',
      rating: 4.8,
      students: 1250,
      type: 'quick-course',
      modules: 1,
      href: '/quick-course/banking-system'
    },
    {
      id: 'credit-basics',
      title: 'Understanding Credit Scores',
      description: 'Everything you need to know about building and maintaining good credit.',
      duration: '15 min',
      level: 'Beginner',
      progress: 0,
      whyThis: 'Essential for future financial opportunities',
      category: 'Banking & Credit',
      rating: 4.9,
      students: 890,
      type: 'quick-course',
      modules: 1,
      href: '/quick-course/credit-basics'
    },
    {
      id: 'budgeting-start',
      title: 'Budgeting for a Fresh Start',
      description: 'See where your money goes, pay needs first, and start a small emergency cushion.',
      duration: 'About 45 min',
      level: 'Beginner',
      progress: 0,
      whyThis: 'A free path you can finish, with four short lessons.',
      category: 'Banking & Credit',
      rating: 4.8,
      students: 0,
      type: 'learning-path',
      modules: 2,
      href: '/courses/budgeting-start'
    }
  ];

  const browseCategories = [
    { name: 'Banking & Credit', count: 12, icon: '🏦' },
    { name: 'Taxes', count: 8, icon: '📊' },
    { name: 'Housing', count: 15, icon: '🏠' },
    { name: 'Healthcare', count: 10, icon: '🏥' },
    { name: 'Employment', count: 18, icon: '💼' },
    { name: 'Education', count: 14, icon: '🎓' },
    { name: 'Legal Rights', count: 9, icon: '⚖️' },
    { name: 'Community Support', count: 11, icon: '🌍' }
  ];

  const savedItems = [
    {
      id: 1,
      title: 'Emergency Fund Planning',
      type: 'course',
      progress: 60,
      savedDate: '2 days ago'
    },
    {
      id: 2,
      title: 'Understanding Health Insurance',
      type: 'guide',
      progress: 0,
      savedDate: '1 week ago'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-4 py-4">
          <PageHeader
            title="Learn"
            description="Discover personalized learning recommendations and courses"
            icon={<BookOpen className="h-6 w-6 text-blue-600" />}
          />
        </div>

        {/* Search Bar */}
        <div className="px-4 pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search courses, guides, and topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1">
              <Filter className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Course Type Filters */}
        <div className="px-4 pb-4">
          <div className="flex space-x-2">
            <button className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg font-medium text-sm">
              All Content
            </button>
            <button className="flex-1 py-2 px-4 bg-white text-blue-600 border border-blue-200 rounded-lg font-medium text-sm hover:bg-blue-50">
              Learning Paths
            </button>
            <button className="flex-1 py-2 px-4 bg-white text-orange-600 border border-orange-200 rounded-lg font-medium text-sm hover:bg-orange-50">
              Quick Courses
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 pb-4">
          <div className="flex space-x-1 bg-gray-100 rounded-xl p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 flex items-center justify-center py-2 px-3 rounded-lg transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">{tab.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6 space-y-6">
        {activeTab === 'recommendations' && (
          <div className="space-y-6">
            {/* AI Recommendations Header */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-100">
              <div className="flex items-center mb-2">
                <Sparkles className="w-5 h-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">AI-Powered Recommendations</h3>
              </div>
              <p className="text-sm text-gray-600">
                Personalized content based on your profile, progress, and goals
              </p>
            </div>

            {/* AI Components - Mobile Optimized */}
            <div className="space-y-4">
              {/* Smart Recommendations */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex items-center mb-3">
                  <TrendingUp className="w-5 h-5 text-green-600 mr-2" />
                  <h4 className="font-semibold text-gray-900">Smart Recommendations</h4>
                </div>
                <SmartRecommendations
                  userProfile={{
                    id: user?.id || '1',
                    currentStage: userType?.stage || 'getting-started',
                    learningStyle: userType?.learningStyle || 'visual',
                    primaryGoals: userType?.primaryGoals || ['financial-stability'],
                    timeCommitment: userType?.timeCommitment || '5-10-minutes',
                    englishLevel: userType?.englishLevel || 'intermediate',
                    studyProgress: {
                      completed_lessons: profile?.study_progress?.completed_lessons || 0,
                      total_lessons: profile?.study_progress?.total_lessons || 24,
                      current_streak: profile?.study_progress?.current_streak || 0,
                      total_points: profile?.study_progress?.total_points || 0
                    }
                  }}
                  onRecommendationClick={(recommendation) => {
                    console.log('Recommendation clicked:', recommendation);
                    // Handle recommendation click
                  }}
                />
              </div>

              {/* AI Content Engine */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex items-center mb-3">
                  <BookOpen className="w-5 h-5 text-blue-600 mr-2" />
                  <h4 className="font-semibold text-gray-900">AI Content Engine</h4>
                </div>
                <AIContentRecommendationEngine
                  userProfile={{
                    id: user?.id || '1',
                    currentLocation: profile?.current_location || 'New York',
                    interests: userType?.interests || ['financial-literacy', 'career-development'],
                    challenges: userType?.challenges || ['language-barrier', 'time-management'],
                    goals: userType?.primaryGoals || ['financial-stability', 'career-growth'],
                    learningStyle: userType?.learningStyle || 'visual',
                    languagePreference: profile?.preferred_language || 'en',
                    currentMood: 'motivated',
                    experienceLevel: userType?.stage || 'getting-started',
                    studyProgress: {
                      completed_lessons: profile?.study_progress?.completed_lessons || 0,
                      total_lessons: profile?.study_progress?.total_lessons || 24,
                      current_streak: profile?.study_progress?.current_streak || 0,
                      total_points: profile?.study_progress?.total_points || 0
                    }
                  }}
                  onContentClick={(content) => {
                    console.log('Content clicked:', content);
                    // Handle content click
                  }}
                />
              </div>

              {/* Proactive Guidance */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex items-center mb-3">
                  <Target className="w-5 h-5 text-purple-600 mr-2" />
                  <h4 className="font-semibold text-gray-900">Proactive Guidance</h4>
                </div>
                <ProactiveGuidanceSystem
                  userProfile={{
                    id: user?.id || '1',
                    currentLocation: profile?.current_location || 'New York',
                    interests: userType?.interests || ['financial-literacy'],
                    challenges: userType?.challenges || ['language-barrier'],
                    goals: userType?.primaryGoals || ['financial-stability'],
                    learningStyle: userType?.learningStyle || 'visual',
                    currentMood: 'motivated',
                    experienceLevel: userType?.stage || 'getting-started',
                    completedLessons: [],
                    currentStreak: 0,
                    totalPoints: 0,
                    lastActive: new Date()
                  }}
                  userBehavior={{
                    completedItems: [],
                    timeSpent: {},
                    ratings: {},
                    searchHistory: [],
                    clickHistory: [],
                    bounceRate: {},
                    completionRate: {},
                    preferredCategories: [],
                    preferredDifficulty: 'beginner',
                    preferredTimeSlots: ['morning'],
                    progressEngagement: {},
                    lastGuidanceInteraction: new Date(),
                    guidanceHistory: []
                  }}
                  onGuidanceAction={(guidance) => {
                    console.log('Guidance action:', guidance);
                    // Handle guidance action
                  }}
                  onDismissGuidance={(guidanceId) => {
                    console.log('Dismiss guidance:', guidanceId);
                    // Handle guidance dismissal
                  }}
                />
              </div>
            </div>

            {/* Recommendations List */}
            <div className="space-y-4">
              {recommendations.map((item) => (
                <ModernCard key={item.id} className={`p-4 border-l-4 ${
                  item.type === 'learning-path' ? 'border-l-blue-500' : 'border-l-orange-500'
                }`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center mb-2 flex-wrap gap-2">
                        <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                          {item.category}
                        </span>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          item.type === 'learning-path' 
                            ? 'text-blue-600 bg-blue-100' 
                            : 'text-orange-600 bg-orange-100'
                        }`}>
                          {item.type === 'learning-path' ? 'Learning Path' : 'Quick Course'}
                        </span>
                        <div className="flex items-center text-yellow-500">
                          <Star className="w-4 h-4 fill-current" />
                          <span className="text-sm font-medium ml-1">{item.rating}</span>
                        </div>
                        <span className="text-sm text-gray-500">({item.students} students)</span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-sm mb-3">{item.description}</p>
                      <div className="flex items-center text-sm text-gray-500 mb-3">
                        <Clock className="w-4 h-4 mr-1" />
                        <span className="mr-4">{item.duration}</span>
                        <Target className="w-4 h-4 mr-1" />
                        <span className="mr-4">{item.level}</span>
                        {item.type === 'learning-path' && (
                          <>
                            <BookOpen className="w-4 h-4 mr-1" />
                            <span>{item.modules} modules</span>
                          </>
                        )}
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                        <div className="flex items-start">
                          <TrendingUp className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-green-800">Why this?</p>
                            <p className="text-sm text-green-700">{item.whyThis}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    <button 
                      onClick={() => navigate(item.href)}
                      className={`flex-1 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center ${
                        item.type === 'learning-path' 
                          ? 'bg-blue-600 hover:bg-blue-700' 
                          : 'bg-orange-600 hover:bg-orange-700'
                      }`}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      {item.type === 'learning-path' ? 'Start Path' : 'Start Course'}
                    </button>
                    <button 
                      onClick={() => {
                        // TODO: Implement bookmark functionality
                        console.log('Bookmark clicked for:', item.id);
                      }}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Bookmark className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </ModernCard>
              ))}
            </div>

            {/* View All Recommendations */}
            <button 
              onClick={() => setActiveTab('browse')}
              className="w-full py-3 text-blue-600 font-medium border border-blue-200 rounded-xl hover:bg-blue-50 transition-colors"
            >
              View All Recommendations
            </button>
          </div>
        )}

        {activeTab === 'browse' && (
          <div className="space-y-6">
            {/* Category Pills */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Browse by Category</h3>
              <div className="grid grid-cols-2 gap-3">
                {browseCategories.map((category, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      // Navigate to guides with category filter
                      navigate('/guides', { state: { category: category.name } });
                    }}
                    className="p-4 bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-200 text-left"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-2xl">{category.icon}</span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                    <h4 className="font-medium text-gray-900 mb-1">{category.name}</h4>
                    <p className="text-sm text-gray-500">{category.count} items</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Learning Paths */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Complete Learning Paths</h3>
              <div className="space-y-4">
                <ModernCard className="p-4 border-l-4 border-l-blue-500">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-3">
                        <Award className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="flex items-center mb-1">
                          <h4 className="font-semibold text-gray-900 mr-2">Budgeting for a Fresh Start</h4>
                          <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                            Learning Path
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">2 modules • 4 lessons • Free</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="w-16 h-16 relative">
                        <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#e5e7eb"
                            strokeWidth="2"
                          />
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth="2"
                            strokeDasharray="0, 100"
                          />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-blue-600">
                          0%
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => navigate('/courses/budgeting-start')}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                    >
                      Start Path
                    </button>
                    <button 
                      onClick={() => console.log('Bookmark Financial Foundation')}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Bookmark className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </ModernCard>

                <ModernCard className="p-4 border-l-4 border-l-green-500">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-3">
                        <Target className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <div className="flex items-center mb-1 flex-wrap gap-2">
                          <h4 className="font-semibold text-gray-900 mr-2">Career Success</h4>
                          <ComingSoonBadge />
                          <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full">
                            Learning Path
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">6 modules • 1.5 hours • Career-focused</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="w-16 h-16 relative">
                        <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#e5e7eb"
                            strokeWidth="2"
                          />
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#10b981"
                            strokeWidth="2"
                            strokeDasharray="0, 100"
                          />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-green-600">
                          0%
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      disabled
                      className="flex-1 bg-gray-300 text-gray-600 py-2 px-4 rounded-lg font-medium cursor-not-allowed"
                    >
                      Coming Soon
                    </button>
                    <button 
                      onClick={() => console.log('Bookmark Career Success')}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Bookmark className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </ModernCard>
              </div>
            </div>

            {/* Quick Courses */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Courses (Bite-Sized)</h3>
              <div className="space-y-3">
                <ModernCard className="p-4 border-l-4 border-l-orange-500">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mr-3">
                        <Clock className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <div className="flex items-center mb-1">
                          <h4 className="font-semibold text-gray-900 mr-2">Banking Basics</h4>
                          <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">
                            Quick Course
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">15 min • Single lesson • Beginner</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center text-sm text-gray-500">
                        <Star className="w-4 h-4 text-yellow-500 mr-1" />
                        <span>4.8</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => navigate('/quick-course/banking-system')}
                      className="flex-1 bg-orange-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-orange-700 transition-colors"
                    >
                      Start Course
                    </button>
                    <button 
                      onClick={() => console.log('Bookmark Banking Basics')}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Bookmark className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </ModernCard>

                <ModernCard className="p-4 border-l-4 border-l-purple-500">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mr-3">
                        <TrendingUp className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <div className="flex items-center mb-1">
                          <h4 className="font-semibold text-gray-900 mr-2">Credit Score 101</h4>
                          <span className="text-xs bg-purple-100 text-purple-600 px-2 py-1 rounded-full">
                            Quick Course
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">20 min • Single lesson • Intermediate</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center text-sm text-gray-500">
                        <Star className="w-4 h-4 text-yellow-500 mr-1" />
                        <span>4.9</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => navigate('/quick-course/credit-basics')}
                      className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-purple-700 transition-colors"
                    >
                      Start Course
                    </button>
                    <button 
                      onClick={() => console.log('Bookmark Credit Score 101')}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Bookmark className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </ModernCard>

                <ModernCard className="p-4 border-l-4 border-l-red-500">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-3">
                        <FileText className="w-6 h-6 text-red-600" />
                      </div>
                      <div>
                        <div className="flex items-center mb-1">
                          <h4 className="font-semibold text-gray-900 mr-2">Tax Filing Basics</h4>
                          <ComingSoonBadge className="ml-2" />
                          <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full ml-2">
                            Quick Course
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">25 min • Single lesson • Beginner</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center text-sm text-gray-500">
                        <Star className="w-4 h-4 text-yellow-500 mr-1" />
                        <span>4.7</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      disabled
                      className="flex-1 bg-gray-300 text-gray-600 py-2 px-4 rounded-lg font-medium cursor-not-allowed"
                    >
                      Coming Soon
                    </button>
                    <button 
                      onClick={() => console.log('Bookmark Tax Filing Basics')}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Bookmark className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </ModernCard>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'saved' && (
          <div className="space-y-4">
            {savedItems.length > 0 ? (
              savedItems.map((item) => (
                <ModernCard key={item.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                        {item.type === 'course' ? (
                          <BookOpen className="w-5 h-5 text-gray-600" />
                        ) : (
                          <MessageSquare className="w-5 h-5 text-gray-600" />
                        )}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{item.title}</h4>
                        <p className="text-sm text-gray-500">Saved {item.savedDate}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {item.progress > 0 && (
                        <div className="text-right">
                          <div className="text-sm font-medium text-gray-900">{item.progress}%</div>
                          <div className="w-16 h-2 bg-gray-200 rounded-full">
                            <div 
                              className="h-2 bg-blue-600 rounded-full" 
                              style={{ width: `${item.progress}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                      <button 
                        onClick={() => {
                          // Navigate to saved item detail
                          console.log('Navigate to saved item:', item.id);
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </ModernCard>
              ))
            ) : (
              <div className="text-center py-12">
                <Bookmark className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No saved items yet</h3>
                <p className="text-gray-500 mb-4">Save courses and guides to access them later</p>
                <button 
                  onClick={() => setActiveTab('browse')}
                  className="bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Browse Courses
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Floating Action Button for Mini Angel */}
      <button 
        onClick={() => navigate('/chat')}
        className="fixed bottom-28 right-4 md:bottom-8 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-40"
      >
        <MessageSquare className="w-6 h-6" />
      </button>
    </div>
  );
};

export default LearnPage;
